<script setup lang="ts">
import type { VoixCheckboxFieldInterface, VoixMediaFieldInterface, VoixTextFieldInterface, VoixWysiwygFieldInterface } from '@voix/types'
import { useScrollLock } from '@vueuse/core'

defineProps<{
  fields: {
    shape: VoixTextFieldInterface
    strokes: VoixCheckboxFieldInterface
    zoom: VoixCheckboxFieldInterface
    blur: VoixCheckboxFieldInterface
    desaturate: VoixCheckboxFieldInterface
    media: VoixMediaFieldInterface
    localMedia: VoixTextFieldInterface
    learnMoreSubtitle: VoixTextFieldInterface
    learnMoreTitle: VoixTextFieldInterface
    learnMoreCopy: VoixWysiwygFieldInterface
  }
}>()

defineSlice({
  name: { label: 'Media Illustration', group: 'Illustrations', layouts: ['VoixMarketing'] },
  description: '',
  preview: 'SlicesIllustrationsImage.jpg',
  fields: {
    shape: {
      type: 'select',
      label: 'Shape',
      options: {
        circle: 'Circle',
        square: 'Square',
        triangle: 'Triangle',
        diamond: 'Diamond',
      },
      default: 'circle',
    },
    strokes: {
      type: 'checkbox',
      label: 'Strokes',
      default: true,
    },
    zoom: {
      type: 'checkbox',
      label: 'Zoom',
      default: false,
    },
    blur: {
      type: 'checkbox',
      label: 'Blur',
      default: false,
    },
    desaturate: {
      type: 'checkbox',
      label: 'Desaturate',
      default: false,
    },
    media: {
      type: 'media',
      label: 'Media',
      breakpoints: {
        default: {
          width: 320,
          height: 320,
        },
        md: {
          width: 768,
          height: 768,
        },
        lg: {
          width: 1024,
          height: 1024,
        },
      },
    },
    localMedia: {
      type: 'text',
      label: 'Local Media',
      enabled: false,
    },
    learnMoreSubtitle: {
      type: 'text',
      label: 'Learn More Subtitle',
      group: 'Learn More',
    },
    learnMoreTitle: {
      type: 'text',
      label: 'Learn More Title',
      group: 'Learn More',
    },
    learnMoreCopy: {
      type: 'wysiwyg',
      label: 'Learn More Copy',
      group: 'Learn More',
      enabled: false,
    },
  },
  templates: [{
    label: 'Main Example',
    fields: {
      media: {
        value: {
          url: 'https://images.unsplash.com/photo-1612838320302-4b3b3b3b3b3b',
        },
      },
    },
  }],
})

const modalOpen = ref(false)
const el = ref<HTMLElement | null>(null)
const isLocked = useScrollLock(el)
onMounted(() => {
  el.value = document.body as HTMLElement
})

function openModal() {
  modalOpen.value = true
  isLocked.value = true
}

function closeModal() {
  modalOpen.value = false
  isLocked.value = false
}
</script>

<template>
  <div class="flex justify-center items-center">
    <div
      class="group aspect-square w-full max-w-sm" :class="{
        'circle-mask': fields.shape.value === 'circle',
        'square-mask': fields.shape.value === 'square',
        'triangle-mask': fields.shape.value === 'triangle',
        'diamond-mask': fields.shape.value === 'diamond',
        'strokes': fields.strokes.value,
      }"
    >
      <div class="absolute inset-0 z-10 flex justify-center items-center">
        <div v-if="fields.shape.value === 'circle' || fields.shape.value === 'square'" class="inner-shape" />
      </div>
      <div
        class="inner-media" :class="{
          'slice-desaturate': fields.desaturate.value,
          'slice-blur': fields.blur.value,
        }"
      >
        <video
          v-if="fields.localMedia.enabled && fields.localMedia.value" class="w-full h-full object-cover " autoplay
          loop muted :class="{ 'group-hover:scale-125 transition-all duration-500 ease-out': fields.zoom.value }"
          playsinline
        >
          <source :src="fields.localMedia.value" type="video/mp4">
        </video>
        <VoixMedia
          v-else :field="fields.media" class="w-full h-full object-cover"
          :class="{ 'group-hover:scale-125 transition-all duration-500 ease-out': fields.zoom.value }"
        />
      </div>

      <button
        v-if="fields.learnMoreCopy.enabled" class="absolute inset-0 z-40 flex justify-center items-center"
        @click="openModal"
      >
        <Icon name="heroicons:magnifying-glass-20-solid" class="w-12 h-12 text-voix-stroke" />
      </button>
    </div>

    <Teleport v-if="fields.learnMoreCopy.enabled && modalOpen" to="#portal-modal">
      <InterfaceModal>
        <div class="relative bg-white max-w-screen max-h-screen overflow-y-scroll text-black grid lg:grid-cols-2 mx-2">
          <button
            class="absolute z-20 top-4 left-4 pl-4 pr-2 py-2 space-x-2 bg-voix-900 text-white border-2 border-white rounded-full"
            @click="closeModal"
          >
            <span class="font-sans uppercase text-xs">Close</span>
            <Icon name="heroicons:x-circle" class="w-8 h-8" />
          </button>
          <div>
            <video
              v-if="fields.localMedia.enabled && fields.localMedia.value" class="w-full h-full object-cover "
              autoplay loop muted
              :class="{ 'group-hover:scale-125 transition-all duration-500 ease-out': fields.zoom.value }" playsinline
            >
              <source :src="fields.localMedia.value" type="video/mp4">
            </video>
            <VoixMedia
              v-else :field="fields.media" class="w-full h-full object-cover"
              :class="{ 'group-hover:scale-125 transition-all duration-500 ease-out': fields.zoom.value }"
            />
          </div>
          <div class="relative flex justify-start items-end p-24">
            <div class="prose">
              <h1>
                <span class="block text-xs font-light font-sans uppercase tracking-loose text-gray-700">
                  {{ fields.learnMoreSubtitle.value }}
                </span>
                {{ fields.learnMoreTitle.value }}
              </h1>

              <div class="font-sans" v-html="fields.learnMoreCopy.value" />
            </div>
          </div>
        </div>
      </InterfaceModal>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.circle-mask,
.square-mask,
.triangle-mask,
.diamond-mask {
  @apply relative overflow-hidden;

  .inner-media {
    @apply absolute inset-0 z-0;
  }

  .slice-blur {
    @apply blur-lg transition-all duration-500;
  }

  .slice-desaturate {
    @apply saturate-0 transition-all duration-500;
  }

  &:hover {
    .slice-blur {
      @apply blur-0;
    }

    .slice-desaturate {
      @apply saturate-100;
    }
  }
}

.circle-mask {
  mask: url('/svgs/shapes/circle.svg');
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  mask-position: center;

  &.strokes {
    @apply border-8 border-voix-stroke rounded-full;

    .inner-shape {
      @apply w-4/5 h-4/5 rounded-full border-4 border-voix-stroke transition-all;
    }

    &:hover {
      .inner-shape {
        @apply w-full h-full border-0
      }
    }
  }
}

.square-mask {
  mask: url('/svgs/shapes/square.svg');
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  mask-position: center;

  &.strokes {
    @apply border-8 border-voix-stroke;

    .inner-shape {
      @apply w-4/5 h-4/5 border-4 border-voix-stroke transition-all;
    }

    &:hover {
      .inner-shape {
        @apply w-full h-full border-0
      }
    }
  }
}

.triangle-mask {
  mask: url('/svgs/shapes/triangle.svg');
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  mask-position: center;
}

.diamond-mask {
  mask: url('/svgs/shapes/diamond.svg');
  mask-size: 100% 100%;
  mask-repeat: no-repeat;
  mask-position: center;
}
</style>
